"use client";
import React from "react";
import Link from "next/link";

export type ButtonVariant = "primary" | "secondary";

interface ButtonProps {
  children: React.ReactNode;
  variant?: ButtonVariant;
  href?: string;
  type?: "button" | "submit";
  disabled?: boolean;
  onClick?: () => void;
  className?: string;
  ariaLabel?: string;
}

function Button({
  children,
  variant = "primary",
  href,
  type = "button",
  disabled = false,
  onClick,
  className = "",
  ariaLabel,
}: ButtonProps) {
  const baseClasses =
    "inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold text-white transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap";

  const variantClasses = {
    primary:
      "bg-[#7f56d9] hover:bg-[#6d47b3] focus:ring-[#a78bfa] shadow-sm",
    secondary:
      "border-2 border-[#a78bfa] hover:bg-[#7f56d9]/10 focus:ring-[#a78bfa]",
  };

  const combinedClassName = `${baseClasses} ${variantClasses[variant]} ${className}`.trim();

  if (href) {
    const isExternal = href.startsWith("http://") || href.startsWith("https://");
    
    if (isExternal) {
      return (
        <a
          href={href}
          className={combinedClassName}
          aria-label={ariaLabel}
          target="_blank"
          rel="noopener noreferrer"
        >
          {children}
        </a>
      );
    }

    return (
      <Link href={href} className={combinedClassName} aria-label={ariaLabel}>
        {children}
      </Link>
    );
  }

  return (
    <button
      type={type}
      className={combinedClassName}
      disabled={disabled}
      onClick={onClick}
      aria-label={ariaLabel}
    >
      {children}
    </button>
  );
}

export default Button;

